import { Radio, Typography } from '@montugroup/design-system';
import { PaymentRequestType } from '@montugroup/pms-api-contracts/build/api-pms/consultation/contracts/request-payment.contract';
import { Box, FormControlLabel, RadioGroup, Stack } from '@mui/material';

enum DoctorConsultChargeRadioValue {
  Billable = 'billable',
  Complimentary = 'complimentary',
  Empty = '',
}

interface ConsultChargeDoctorProps {
  isConcessionPatient?: boolean;
  onSelectionChange?: (paymentRequestType: PaymentRequestType | null) => void;
  selectedPaymentRequestType: PaymentRequestType | null;
  isComplimentaryForDoctor: boolean;
  handleIsComplimentaryForDoctorChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isInitiallyMarkedAsComplimentary: boolean;
}

const RadioItem = ({ value, label, description }: { value: string; label: string; description: string }) => (
  <FormControlLabel
    value={value}
    control={<Radio sx={{ marginTop: -4 }} />}
    label={
      <Box>
        <Typography variant="body1">{label}</Typography>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </Box>
    }
  />
);

const ConsultChargeDoctor = ({
  isConcessionPatient = false,
  onSelectionChange,
  selectedPaymentRequestType,
  handleIsComplimentaryForDoctorChange,
  isInitiallyMarkedAsComplimentary,
}: ConsultChargeDoctorProps) => {
  const getRadioValue = () => {
    if (selectedPaymentRequestType === PaymentRequestType.COMPLIMENTARY) {
      return DoctorConsultChargeRadioValue.Complimentary;
    } else if (
      selectedPaymentRequestType === PaymentRequestType.STANDARD ||
      selectedPaymentRequestType === PaymentRequestType.CONCESSION
    ) {
      return DoctorConsultChargeRadioValue.Billable;
    }
    return DoctorConsultChargeRadioValue.Empty;
  };

  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    const isComplimentary = newValue === DoctorConsultChargeRadioValue.Complimentary;
    handleIsComplimentaryForDoctorChange({
      target: { checked: isComplimentary },
    } as React.ChangeEvent<HTMLInputElement>);

    if (onSelectionChange) {
      if (newValue === DoctorConsultChargeRadioValue.Billable) {
        const paymentRequestType = isConcessionPatient ? PaymentRequestType.CONCESSION : PaymentRequestType.STANDARD;
        onSelectionChange(paymentRequestType);
      } else if (newValue === DoctorConsultChargeRadioValue.Complimentary) {
        onSelectionChange(PaymentRequestType.COMPLIMENTARY);
      } else if (newValue === DoctorConsultChargeRadioValue.Empty) {
        onSelectionChange(null);
      }
    }
  };

  return (
    <Stack direction="column" spacing={4} mt={4} data-testid="consult-charge-doctor">
      <Typography>Select the charge type for the consultation before proceeding to review script.</Typography>

      <RadioGroup value={getRadioValue()} onChange={handleRadioChange}>
        <Stack direction="column" spacing={4} marginTop={1}>
          <RadioItem
            value={DoctorConsultChargeRadioValue.Billable}
            label="Billable consultation"
            description="Concession and other eligible discounts will be applied automatically."
          />

          <RadioItem
            value={DoctorConsultChargeRadioValue.Complimentary}
            label="Complimentary consultation"
            description={
              isInitiallyMarkedAsComplimentary
                ? 'The nurse has pre-selected complimentary consultation.'
                : 'The patient will not be charged.'
            }
          />
        </Stack>
      </RadioGroup>
    </Stack>
  );
};

export { ConsultChargeDoctor, DoctorConsultChargeRadioValue };
